import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  CheckCircle2, 
  Loader2, 
  XCircle,
  Shield,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [message, setMessage] = useState('Verifying your payment...');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && user) {
      pollPaymentStatus();
    } else if (!sessionId) {
      setStatus('failed');
      setMessage('No payment session found.');
    }
  }, [sessionId, user]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('failed');
      setMessage('Payment verification timed out. Please check your email for confirmation or contact support.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setMessage(data.message || 'Payment successful! Your account has been upgraded to Premium.');
        updateUser({ is_premium: true });
        toast.success('Welcome to Premium!');
      } else if (data.status === 'expired') {
        setStatus('failed');
        setMessage('Payment session expired. Please try again.');
      } else {
        // Still pending, continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      
      if (attempts < maxAttempts - 1) {
        setTimeout(pollPaymentStatus, 2000);
      } else {
        setStatus('failed');
        setMessage('Error verifying payment. Please contact support.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center py-12 px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {/* Status Icon */}
        <div className="mb-8">
          {status === 'checking' && (
            <div className="w-20 h-20 mx-auto rounded-full bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#00FF94] animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="w-20 h-20 mx-auto rounded-full bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center animate-pulse-glow">
              <CheckCircle2 className="w-10 h-10 text-[#00FF94]" />
            </div>
          )}
          {status === 'failed' && (
            <div className="w-20 h-20 mx-auto rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
          )}
        </div>

        {/* Title */}
        <h1 className="font-heading font-bold text-2xl text-white mb-4">
          {status === 'checking' && 'Processing Payment...'}
          {status === 'success' && 'Payment Successful!'}
          {status === 'failed' && 'Payment Issue'}
        </h1>

        {/* Message */}
        <p className="text-gray-400 mb-8">
          {message}
        </p>

        {/* Premium Badge (on success) */}
        {status === 'success' && (
          <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-6 mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-[#00FF94]" />
              <span className="font-heading font-semibold text-white text-lg">Premium Activated</span>
            </div>
            <ul className="text-sm text-gray-400 space-y-2 text-left">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#00FF94]" />
                Unlimited AI Assistant access
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#00FF94]" />
                Advanced protection guides
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#00FF94]" />
                Priority support
              </li>
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {status === 'success' && (
            <>
              <Link to="/dashboard">
                <Button className="w-full btn-primary" data-testid="go-to-dashboard">
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/chat">
                <Button variant="outline" className="w-full border-[#262626] text-gray-400 hover:text-white hover:bg-white/5">
                  Try AI Assistant
                </Button>
              </Link>
            </>
          )}
          {status === 'failed' && (
            <>
              <Link to="/pricing">
                <Button className="w-full btn-primary" data-testid="try-again">
                  Try Again
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="w-full border-[#262626] text-gray-400 hover:text-white hover:bg-white/5">
                  Back to Dashboard
                </Button>
              </Link>
            </>
          )}
          {status === 'checking' && (
            <p className="text-xs text-gray-600">
              Please wait while we verify your payment...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
